import type { PartType, PointType } from '@/partTypes'
import store from '@/store'
import { fourTrimEdge } from '@/util/commonFuncs'
import {
  getPathMinMaxPoint,
  getSlotBasicInfo,
  rotateBigPlank,
} from '@/util/plankCommonFuncs'
import { getGraghEdge } from '@/util/tag'
import rollLShapePlank from '@/util/tag/pdf-draw'
import { processLabelFn } from '@/views/materialsList/utils/show'
import { transformData } from '@/views/newPaiban/util/transformData'
import {
  DxfWriter,
  LWPolylineFlags,
  TextHorizontalAlignment,
  Units,
  point2d,
  point3d,
} from '@tarikjabiri/dxf'
import FileSaver from 'file-saver'
import JSZip from 'jszip'
import { cloneDeep } from 'lodash'

import { backFeatureSpec, frontFeatureSpec, outlineSpec } from './ocs'
import type { Dxf3DSpec } from './ocs'
import { resolveProfile } from './profileResolver'
import { ALL_LAYER_ROLES, DxfLayerRole, has3D } from './types'
import type { DxfProfile, DxfProfileId } from './types'

const STYLE_CHINESE = 'Chinese'
const MULTI_PAGE_START_X = 32
const DXF_TEXT_SCALE = 1
const DXF_PART_TEXT_SCALE = 0.7
const ASCII_TEXT_WIDTH_RATIO = 0.55
const MULTI_PLANK_GAP = 50
const OVERFLOW_OFFSET = 10
const CANVAS_PADDING = 100
const PART_TEXT_MIN_PADDING = 2
const PART_TEXT_MIN_LINE_SPACE = 1.5

const texDirMap: Record<string, string> = {
  normal: '竖纹',
  notcare: '无纹理',
  reverse: '横纹',
}

type RectType = {
  height: number
  key?: string
  left: number
  top: number
  width: number
}

type DxfExportConfig = {
  fileName?: string
  folderName?: string
  onePageNum?: number
  save?: boolean
  total?: number
  zip?: JSZip
  /** Profile id to use; absent/empty → general-2d-v1. Unknown ids throw. */
  profileId?: DxfProfileId
}

type DxfPart = PartType & {
  partCenterX: number
  partCenterY: number
}

type PlankLayout = {
  plank: any
  originPlank: any
  finalMinX: number
  finalMinY: number
  finalPath: PointType[]
  finalPlankHeight: number
  finalPlankWidth: number
  rotatedMinX: number
  rotatedMinY: number
  startX: number
  startY: number
  bigPlankTextHeight: number
}

function normalizeRotate(raw: unknown): number {
  if (raw == null || raw === '' || raw === 0 || raw === '0') return 0
  const angle = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw)
  return Number.isNaN(angle) ? 0 : angle
}

function isTrueRotate(raw: unknown): raw is 90 | 180 | 270 {
  const angle = normalizeRotate(raw)
  return angle === 90 || angle === 180 || angle === 270
}

function addChineseStyle(dxf: DxfWriter) {
  const style = dxf.tables.styleTable.addStyle(STYLE_CHINESE)
  style.fontFileName = 'simsun.ttc'
  style.bigFontFileName = 'gbcbig.shx'
}

function addStandardLayers(dxf: DxfWriter, profile: DxfProfile) {
  for (const role of ALL_LAYER_ROLES) {
    const def = profile.layerMap[role]
    dxf.addLayer(def.name, def.color, def.lineType)
  }
}

function getDxfTextHeight(fontSize: number): number {
  return fontSize * DXF_TEXT_SCALE
}

function getTextLines(text: string): string[] {
  return String(text || '').split('\n')
}

function getCharWidth(ch: string, fontSize: number): number {
  return ch.charCodeAt(0) > 127 ? fontSize : fontSize * ASCII_TEXT_WIDTH_RATIO
}

function estimateFabricTextWidth(text: string, fontSize: number): number {
  return getTextLines(text).reduce((maxWidth, line) => {
    let width = 0
    for (const ch of line) {
      width += getCharWidth(ch, fontSize)
    }
    return Math.max(maxWidth, width)
  }, 0)
}

function estimateFabricTextHeight(
  text: string,
  fontSize: number,
  lineSpace = 1
): number {
  return getTextLines(text).length * fontSize * lineSpace
}

function wrapTextByWidth(
  text: string,
  width: number,
  fontSize: number
): string {
  if (!width || width <= 0) return text
  return getTextLines(text)
    .flatMap((line) => {
      let currentLine = ''
      let currentWidth = 0
      const wrappedLines: string[] = []
      for (const ch of line) {
        const charWidth = getCharWidth(ch, fontSize)
        if (currentLine && currentWidth + charWidth > width) {
          wrappedLines.push(currentLine)
          currentLine = ch
          currentWidth = charWidth
        } else {
          currentLine += ch
          currentWidth += charWidth
        }
      }
      wrappedLines.push(currentLine)
      return wrappedLines
    })
    .join('\n')
}

function wrapTextByFabric(
  text: string,
  width: number,
  templatePart: any,
  fontSize: number,
  lineSpace: number
): string {
  const fabricInstance = (window as any).fabric
  if (!fabricInstance?.Textbox) {
    return wrapTextByWidth(text, width, fontSize)
  }
  const textBox = new fabricInstance.Textbox(text, {
    fontFamily: templatePart.fontFamily,
    fontSize,
    fontWeight: templatePart.fontWeight,
    lineHeight: lineSpace,
    splitByGrapheme: true,
    textAlign: 'left',
    width,
  })
  return (textBox._textLines || [])
    .map((line: string[] | string) => {
      return Array.isArray(line) ? line.join('') : line
    })
    .join('\n')
}

function getSafePartTextFontSize(fontSize: unknown): number | null {
  const value = Number(fontSize)
  return Number.isFinite(value) && value > 0 ? value : null
}

function getPartTextLineSpace(lineSpace: unknown): number {
  const value = Number(lineSpace)
  return Number.isFinite(value)
    ? Math.max(value, PART_TEXT_MIN_LINE_SPACE)
    : PART_TEXT_MIN_LINE_SPACE
}

function getPartTextPadding(
  width: number,
  height: number,
  textHeight: number
): number {
  const desiredPadding = Math.max(PART_TEXT_MIN_PADDING, textHeight * 0.5)
  // Keep the minimum padding whenever the part is large enough, while still
  // leaving a usable area for exceptionally small parts.
  const maxPadding = Math.min(width, height) / 2
  return Math.min(desiredPadding, maxPadding)
}

function getDxfMTextValue(text: string): string {
  return String(text || '').replace(/\n/g, '\\P')
}

function addMTextWithStyle(
  dxf: DxfWriter,
  canvasH: number,
  x: number,
  y: number,
  height: number,
  text: string,
  options: any = {}
) {
  const dxfText = getDxfMTextValue(text)
  const entity = dxf.modelSpace.addMText(
    point3d(x, canvasH - y),
    height,
    dxfText,
    options
  )
  ;(entity as any).textStyle = STYLE_CHINESE
  return entity
}

function addTextWithStyle(
  dxf: DxfWriter,
  canvasH: number,
  x: number,
  y: number,
  height: number,
  text: string,
  options: any = {}
) {
  const entity = dxf.modelSpace.addText(
    point3d(x, canvasH - y),
    height,
    text,
    options
  )
  ;(entity as any).textStyle = STYLE_CHINESE
  return entity
}

/** Circle 需在几何组码前写入厚度；库本身不会序列化该属性。 */
function applyCircleThickness(circle: any, thickness: number) {
  const original = circle.dxfyChild as (...args: any[]) => void
  circle.dxfyChild = function (this: any, dx: any) {
    dx.push(39, thickness)
    original.call(this, dx)
  }
}

function addMultilineTextWithStyle(
  dxf: DxfWriter,
  canvasH: number,
  x: number,
  y: number,
  height: number,
  text: string,
  options: any = {}
) {
  const lineHeight = height * (options.lineSpace || 1)
  getTextLines(text).forEach((line, index) => {
    const lineY = y + index * lineHeight
    const insertY = lineY + height
    const textOptions = { ...options }
    if (textOptions.secondAlignmentPoint) {
      textOptions.secondAlignmentPoint = point3d(x, canvasH - insertY)
    }
    delete textOptions.lineSpace
    addTextWithStyle(dxf, canvasH, x, insertY, height, line, textOptions)
  })
}

function addClosedPath(
  dxf: DxfWriter,
  canvasH: number,
  path: PointType[],
  layerName: string,
  spec?: Dxf3DSpec
) {
  if (!path.length) return
  const verts = path.map((p) => ({ point: point2d(p.x, canvasH - p.y) }))
  const entity = dxf.modelSpace.addLWPolyline(verts, {
    flags: LWPolylineFlags.Closed,
    layerName,
    elevation: spec?.elevation ?? 0,
  })
  if (spec) {
    entity.thickness = spec.thickness
  }
}

function addRect(
  dxf: DxfWriter,
  canvasH: number,
  left: number,
  top: number,
  width: number,
  height: number,
  layerName: string,
  spec?: Dxf3DSpec
) {
  const entity = dxf.modelSpace.addRectangle(
    point2d(left, canvasH - top),
    point2d(left + width, canvasH - (top + height)),
    { layerName, elevation: spec?.elevation ?? 0 }
  )
  if (spec) {
    entity.thickness = spec.thickness
  }
}

function addLine(
  dxf: DxfWriter,
  canvasH: number,
  start: PointType,
  end: PointType,
  layerName: string
) {
  dxf.modelSpace.addLine(
    point3d(start.x, canvasH - start.y),
    point3d(end.x, canvasH - end.y),
    { layerName }
  )
}

/** 射线法判断点是否在多边形内（含边界）。 */
function pointInPolygon(
  pt: { x: number; y: number },
  polygon: { x: number; y: number }[]
): boolean {
  // 先检查是否在边界上
  const n = polygon.length
  for (let i = 0; i < n - 1; i++) {
    if (pointOnSegment(pt, polygon[i], polygon[i + 1], 0.01)) return true
  }
  let inside = false
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y
    const xj = polygon[j].x,
      yj = polygon[j].y
    if (
      yi > pt.y !== yj > pt.y &&
      pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside
    }
  }
  return inside
}

/** 归一化多边形：去重连续点，闭合成精确环。 */
function normalizePolygon(
  raw: { x: number; y: number }[],
  eps: number
): { x: number; y: number }[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    throw new Error(`[dxf-export] 路径为空`)
  }
  const pts: { x: number; y: number }[] = []
  for (const p of raw) {
    if (
      typeof p.x !== 'number' ||
      typeof p.y !== 'number' ||
      !Number.isFinite(p.x) ||
      !Number.isFinite(p.y)
    ) {
      throw new Error(`[dxf-export] 路径含非法点`)
    }
    if (
      pts.length === 0 ||
      Math.abs(p.x - pts[pts.length - 1].x) > eps ||
      Math.abs(p.y - pts[pts.length - 1].y) > eps
    ) {
      pts.push({ x: p.x, y: p.y })
    }
  }
  // 非连续重复（仅允许闭合重复）
  for (let i = 0; i < pts.length; i++) {
    for (let j = i + 2; j < pts.length; j++) {
      if (
        Math.abs(pts[i].x - pts[j].x) <= eps &&
        Math.abs(pts[i].y - pts[j].y) <= eps &&
        !(i === 0 && j === pts.length - 1)
      ) {
        throw new Error(`[dxf-export] 路径含非连续重复顶点`)
      }
    }
  }
  // 非闭合顶点
  const nonClosing =
    pts.length >= 2 &&
    Math.abs(pts[0].x - pts[pts.length - 1].x) <= eps &&
    Math.abs(pts[0].y - pts[pts.length - 1].y) <= eps
      ? pts.length - 1
      : pts.length
  if (nonClosing < 3) {
    throw new Error(`[dxf-export] 路径顶点不足(${nonClosing})`)
  }
  // 精确闭合：EPS 内用首点替换，否则追加首点
  const first = pts[0],
    last = pts[pts.length - 1]
  if (Math.abs(first.x - last.x) <= eps && Math.abs(first.y - last.y) <= eps) {
    pts[pts.length - 1] = { x: first.x, y: first.y }
  } else {
    pts.push({ x: first.x, y: first.y })
  }
  // 零面积
  if (Math.abs(polygonArea(pts)) < eps * eps) {
    throw new Error(`[dxf-export] 路径面积过小`)
  }
  // 自交
  if (polygonSelfIntersects(pts, eps)) {
    throw new Error(`[dxf-export] 路径自交`)
  }
  return pts
}

function polygonArea(poly: { x: number; y: number }[]): number {
  let a = 0
  for (let i = 0; i < poly.length - 1; i++) {
    a += poly[i].x * poly[i + 1].y - poly[i + 1].x * poly[i].y
  }
  return a / 2
}

function polygonSelfIntersects(
  poly: { x: number; y: number }[],
  eps: number
): boolean {
  const n = poly.length
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 2; j < n - 1; j++) {
      if (i === 0 && j === n - 2) continue
      if (
        segSegIntersectT(poly[i], poly[i + 1], poly[j], poly[j + 1], eps) !=
        null
      ) {
        return true
      }
    }
  }
  return false
}

/** 按 eps 去重点集。 */
function dedupePoints(
  pts: { x: number; y: number }[],
  eps: number
): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = []
  for (const p of pts) {
    if (
      !out.some((q) => Math.abs(p.x - q.x) <= eps && Math.abs(p.y - q.y) <= eps)
    ) {
      out.push({ x: p.x, y: p.y })
    }
  }
  return out
}

const PARAM_EPS = 1e-9

/**
 * 水平/竖直线段与多边形各边的交点。
 * t ∈ [-EPS, 1+EPS]，clamp 到 [0,1]；EPS 用于近似水平/竖直判定。
 */
function intersectCardinal(
  poly: { x: number; y: number }[],
  coord: number,
  horiz: boolean,
  eps: number
): { x: number; y: number }[] {
  const hits: { x: number; y: number }[] = []
  const n = poly.length
  for (let i = 0; i < n - 1; i++) {
    const a = poly[i],
      b = poly[i + 1]
    if (horiz) {
      if (Math.abs(a.y - b.y) <= eps) {
        if (Math.abs(a.y - coord) <= eps) {
          hits.push({ x: a.x, y: a.y }, { x: b.x, y: b.y })
        }
        continue
      }
      if (
        (a.y > coord + eps && b.y > coord + eps) ||
        (a.y < coord - eps && b.y < coord - eps)
      )
        continue
      let t = (coord - a.y) / (b.y - a.y)
      if (t < -PARAM_EPS || t > 1 + PARAM_EPS) continue
      t = Math.max(0, Math.min(1, t))
      hits.push({ x: a.x + t * (b.x - a.x), y: coord })
    } else {
      if (Math.abs(a.x - b.x) <= eps) {
        if (Math.abs(a.x - coord) <= eps) {
          hits.push({ x: a.x, y: a.y }, { x: b.x, y: b.y })
        }
        continue
      }
      if (
        (a.x > coord + eps && b.x > coord + eps) ||
        (a.x < coord - eps && b.x < coord - eps)
      )
        continue
      let t = (coord - a.x) / (b.x - a.x)
      if (t < -PARAM_EPS || t > 1 + PARAM_EPS) continue
      t = Math.max(0, Math.min(1, t))
      hits.push({ x: coord, y: a.y + t * (b.y - a.y) })
    }
  }
  return dedupePoints(hits, eps)
}

/** 线段 a→b 与多边形边界是否在到达 b 之前相交/触碰/重叠。 */
function segmentHitsPolygon(
  a: { x: number; y: number },
  b: { x: number; y: number },
  poly: { x: number; y: number }[],
  eps: number
): boolean {
  const n = poly.length
  for (let i = 0; i < n - 1; i++) {
    const p = poly[i],
      q = poly[i + 1]
    const t = segSegIntersectT(a, b, p, q, eps)
    if (t != null && t > PARAM_EPS && t < 1 - PARAM_EPS) return true
  }
  return false
}

/** 点是否在 pq 线段上（含端点）。 */
function pointOnSegment(
  pt: { x: number; y: number },
  p: { x: number; y: number },
  q: { x: number; y: number },
  eps: number
): boolean {
  const dx = q.x - p.x,
    dy = q.y - p.y
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len <= eps) {
    const px = pt.x - p.x,
      py = pt.y - p.y
    return Math.sqrt(px * px + py * py) <= eps
  }
  const cross = Math.abs(dx * (pt.y - p.y) - dy * (pt.x - p.x))
  if (cross / len > eps) return false
  const projected = (dx * (pt.x - p.x) + dy * (pt.y - p.y)) / len
  return projected >= -eps && projected <= len + eps
}

function segSegIntersectT(
  a: { x: number; y: number },
  b: { x: number; y: number },
  c: { x: number; y: number },
  d: { x: number; y: number },
  eps: number
): number | null {
  const dx1 = b.x - a.x,
    dy1 = b.y - a.y
  const dx2 = d.x - c.x,
    dy2 = d.y - c.y
  const len1 = Math.sqrt(dx1 * dx1 + dy1 * dy1)
  const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2)

  // 退化线段
  if (len1 <= eps) {
    if (len2 <= eps) {
      return Math.abs(a.x - c.x) <= eps && Math.abs(a.y - c.y) <= eps ? 0 : null
    }
    return pointOnSegment(a, c, d, eps) ? 0 : null
  }
  if (len2 <= eps) {
    return pointOnSegment(c, a, b, eps)
      ? ((c.x - a.x) * dx1 + (c.y - a.y) * dy1) / (len1 * len1)
      : null
  }

  // 归一化叉积判平行
  const denom = dx1 * dy2 - dy1 * dx2
  const normCross = Math.abs(denom) / (len1 * len2)
  if (normCross <= PARAM_EPS) {
    // 共线判定：用垂直距离
    const perpDist = Math.abs(dx1 * (c.y - a.y) - dy1 * (c.x - a.x)) / len1
    if (perpDist > eps) return null
    // 共线：投影区间
    const t_c = ((c.x - a.x) * dx1 + (c.y - a.y) * dy1) / (len1 * len1)
    const t_d = ((d.x - a.x) * dx1 + (d.y - a.y) * dy1) / (len1 * len1)
    const tMin = Math.min(t_c, t_d),
      tMax = Math.max(t_c, t_d)
    if (tMin > 1 + PARAM_EPS || tMax < -PARAM_EPS) return null
    const oS = Math.max(0, tMin),
      oE = Math.min(1, tMax)
    if (oE - oS > PARAM_EPS) {
      return oS > PARAM_EPS ? oS : 0.5
    }
    return null
  }

  const t = ((c.x - a.x) * dy2 - (c.y - a.y) * dx2) / denom
  const u = ((c.x - a.x) * dy1 - (c.y - a.y) * dx1) / denom
  if (
    t >= -PARAM_EPS &&
    t <= 1 + PARAM_EPS &&
    u >= -PARAM_EPS &&
    u <= 1 + PARAM_EPS
  ) {
    return Math.max(0, Math.min(1, t))
  }
  return null
}

/** Liang-Barsky 裁剪：线段是否进入矩形开放内部。 */
function liangBarskyClip(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  rL: number,
  rT: number,
  rR: number,
  rB: number
): boolean {
  const dx = x2 - x1,
    dy = y2 - y1
  const rw = rR - rL,
    rh = rB - rT
  if (rw <= 0 || rh <= 0) return false
  // 内缩量：正数，保证窄矩形仍可检测
  const inset = Math.min(0.005, rw / 4, rh / 4)
  const iL = rL + inset,
    iT = rT + inset,
    iR = rR - inset,
    iB = rB - inset
  if (iR <= iL || iB <= iT) return false
  let t0 = 0,
    t1 = 1
  const p = [-dx, dx, -dy, dy]
  const q = [x1 - iL, iR - x1, y1 - iT, iB - y1]
  for (let i = 0; i < 4; i++) {
    if (Math.abs(p[i]) < 1e-15) {
      if (q[i] < 0) return false
    } else {
      const t = q[i] / p[i]
      if (p[i] < 0) {
        if (t > t1) return false
        if (t > t0) t0 = t
      } else {
        if (t < t0) return false
        if (t < t1) t1 = t
      }
    }
  }
  return t1 - t0 > PARAM_EPS
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunkSize = Math.max(1, Number(size) || 1)
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize))
  }
  return result
}

function handleBigPlankText(plank: any, code: any, total = 1): string {
  if (!code.template_string) return ''
  let str = code.template_string
  const NC_SETTING = cloneDeep(store.state.ncSetting)
  for (const key in code.label_keys) {
    switch (code.label_keys[key]) {
      case 'canvasIndex': {
        const canvasIndex = `${plank.canvasIndex}/${total}`
        str = str.replace(new RegExp(key, 'g'), canvasIndex)
        break
      }
      case 'usedRate': {
        const usedRate = plank.usedRate
          ? plank.usedRate >= 1
            ? `${(100).toFixed(2)}%`
            : `${(plank.usedRate * 100).toFixed(2)}%`
          : '0%'
        str = str.replace(new RegExp(key, 'g'), usedRate)
        break
      }
      case 'size': {
        const { size } = getGraghEdge(plank)
        str = str.replace(new RegExp(key, 'g'), size)
        break
      }
      case 'plankNum': {
        let plankNum = ''
        if (
          plank.surplusInfo &&
          Object.keys(plank.surplusInfo).length &&
          !plank.surplusInfo.isNotSurplus
        ) {
          plankNum = plank.surplusInfo.plankNum
        }
        str = str.replace(new RegExp(key, 'g'), plankNum)
        break
      }
      case 'margin': {
        const plankTrim = plank.isUseSaw
          ? plank.margin
          : store.state.selectStandardPlank.plankEdgeOff
        const margin: any = fourTrimEdge(plankTrim, NC_SETTING, true)
        let text = ''
        if (margin?.toString().includes(',')) {
          const trimEdgeArr = margin.toString().split(',')
          text = `上${trimEdgeArr[0]}-下${trimEdgeArr[1]}-左${trimEdgeArr[2]}-右${trimEdgeArr[3]}`
        } else {
          text = margin
        }
        str = str.replace(new RegExp(key, 'g'), text)
        break
      }
      default:
        str = str.replace(
          new RegExp(key, 'g'),
          plank[code.label_keys[key]] || ''
        )
        break
    }
  }
  return str
}

function handlePartText(part: any, code: any): string {
  if (!code.template_string) return ''
  let str = code.template_string
  for (const key in code.label_keys) {
    switch (code.label_keys[key]) {
      case 'oRect': {
        const oRectSize = `${part.oRect.height}*${part.oRect.width}*${part.thick}`
        str = str.replace(new RegExp(key, 'g'), oRectSize)
        break
      }
      case 'realRect': {
        const realRectSize = `${part.realRect.height.toFixed(
          2
        )}*${part.realRect.width.toFixed(2)}*${part.thick}`
        str = str.replace(new RegExp(key, 'g'), realRectSize)
        break
      }
      case 'specialFlag': {
        const specialMark = part.specialFlag ?? part.specTagTemplate
        const specialMarkMap: Record<string, string> = {
          default: '',
          triangle_shape: '三角形',
          square_shape: '矩形',
          hexagon_shape: '六边形',
          circle_shape: '圆形',
          half_circle_shape: '半圆形',
        }
        str = str.replace(
          new RegExp(key, 'g'),
          specialMarkMap[specialMark] ?? ''
        )
        break
      }
      case 'handle': {
        let handleType = ''
        if (
          part.handle &&
          part.handle.partsChildren &&
          part.handle.partsChildren.length
        ) {
          handleType =
            part.handle.partsChildren[0].maskPartName ||
            part.handle.partsChildren[0].matSpec
              ? `${
                  part.handle.partsChildren[0].maskPartName ||
                  part.handle.partsChildren[0].matSpec
                }_${part.handle.partsChildren[0].name}`
              : ''
        }
        str = str.replace(new RegExp(key, 'g'), handleType)
        break
      }
      case 'process_label': {
        const result = processLabelFn(part, store.state.specialRemarks)
        str = str.replace(new RegExp(key, 'g'), result)
        break
      }
      case 'texDir': {
        str = str.replace(
          new RegExp(key, 'g'),
          texDirMap[part[code.label_keys[key]]] ?? ''
        )
        break
      }
      case 'hingeNum': {
        const hingeNum = part.holes.filter(
          (hole: any) => hole.symbol == 'HINGE'
        )?.length
        str = str.replace(new RegExp(key, 'g'), String(hingeNum))
        break
      }
      case 'partName': {
        const partName = part.partName || part.name
        str = str.replace(new RegExp(key, 'g'), partName)
        break
      }
      default:
        str = str.replace(
          new RegExp(key, 'g'),
          part[code.label_keys[key]] || ''
        )
        break
    }
  }
  return str
}

function adjustRectangles(rectangles: number[][], gap = 5): number[][] {
  if (!rectangles.length) return []
  const adjusted = [rectangles[0]]
  for (let i = 1; i < rectangles.length; i++) {
    const current = [...rectangles[i]]
    let hasOverlap = true
    while (hasOverlap) {
      hasOverlap = false
      for (const rect of adjusted) {
        if (isOverlapping(current, rect)) {
          hasOverlap = true
          current[1] = rect[3] + gap
          current[3] = current[1] + (rectangles[i][3] - rectangles[i][1])
          break
        }
      }
    }
    adjusted.push(current)
  }
  return adjusted
}

function isOverlapping(rec1: number[], rec2: number[]): boolean {
  if (
    rec1[0] === rec1[2] ||
    rec1[1] === rec1[3] ||
    rec2[0] === rec2[2] ||
    rec2[1] === rec2[3]
  ) {
    return false
  }
  return !(
    rec1[2] <= rec2[0] ||
    rec1[0] >= rec2[2] ||
    rec1[3] <= rec2[1] ||
    rec1[1] >= rec2[3]
  )
}

function adjustRectanglesLeft(
  rects: RectType[],
  canvasWidth: number,
  gap = 5,
  dir = 'bottom'
): RectType[] {
  if (!rects.length) return []
  let currentX = 0
  let currentY = 0
  for (const rect of rects) {
    const originOffsetX = rect.left
    const originOffsetY = rect.top
    let drawX = currentX + originOffsetX
    let drawY = originOffsetY
    if (currentY) {
      drawY = currentY
    }
    if (drawX + rect.width > canvasWidth) {
      currentX = 0
      if (dir === 'bottom') {
        currentY += drawY + rect.height + gap
      } else {
        currentY = drawY - gap
      }
      drawX = currentX + originOffsetX
      drawY = dir === 'bottom' ? currentY : currentY - rect.height
    }
    currentX = drawX + rect.width + gap - originOffsetX
    if (dir === 'top') {
      currentY = drawY
    }
    rect.left = drawX
    rect.top = drawY
  }
  return rects
}

function getSlotLayer(
  slot: any,
  frontLayer: string,
  backLayer: string
): string {
  return slot.side === 1 ? frontLayer : backLayer
}

function drawSlot(
  dxf: DxfWriter,
  canvasH: number,
  item: any,
  slot: any,
  startX: number,
  startY: number,
  layerName: string,
  threeD: boolean
) {
  const slotInfo = getSlotBasicInfo(slot, 'pt')
  const panelThick =
    typeof item.thick === 'number' &&
    Number.isFinite(item.thick) &&
    item.thick > 0
      ? item.thick
      : undefined
  const isBack = slot.side === -1
  const spec = threeD
    ? isBack
      ? backFeatureSpec(slotInfo.slotData?.deep, panelThick)
      : frontFeatureSpec(slotInfo.slotData?.deep)
    : undefined

  const path = slotInfo.path ?? []
  if (path.length >= 4) {
    const drawPath = path.map((point: PointType) => ({
      x: startX + item.startX + point.x,
      y: startY + item.startY + point.y,
    }))
    addClosedPath(dxf, canvasH, drawPath, layerName, spec)
    return
  }
  const sx = item.startX + slotInfo.x + startX
  const sy = item.startY + slotInfo.y + startY
  addRect(
    dxf,
    canvasH,
    sx,
    sy,
    slotInfo.width,
    slotInfo.height,
    layerName,
    spec
  )
}

function drawPathList(
  dxf: DxfWriter,
  canvasH: number,
  paths: PointType[][],
  item: any,
  startX: number,
  startY: number,
  layerName: string,
  spec?: Dxf3DSpec
) {
  paths.forEach((path) => {
    const drawPath = path.map((point) => ({
      x: startX + item.startX + point.x,
      y: startY + item.startY + point.y,
    }))
    addClosedPath(dxf, canvasH, drawPath, layerName, spec)
  })
}

function drawOverflowTextAndLine(
  dxf: DxfWriter,
  canvasH: number,
  overflowPart: Record<string, any[]>,
  template: any,
  data: {
    bigPlankTextHeight: number
    plankHeight: number
    plankWidth: number
    startX: number
    startY: number
  },
  /** Layer names resolved from the active profile. */
  profile: DxfProfile,
  partTextFontSize: number | null,
  partTextLineSpace: number
) {
  if (partTextFontSize == null) return

  const { plankWidth, plankHeight, startX, startY } = data
  Object.keys(overflowPart).forEach((dir) => {
    const parts = overflowPart[dir]
    if (!parts.length) return

    const rectMap: Record<string, { part: any; rect: RectType; text: string }> =
      {}
    const partTextHeight = getDxfTextHeight(partTextFontSize)
    const lineSpace = partTextLineSpace

    parts.forEach((item, index) => {
      const partText = handlePartText(item, template.part.code)
      const textWidth = estimateFabricTextWidth(partText, partTextFontSize)
      const textHeight = estimateFabricTextHeight(
        partText,
        partTextFontSize,
        lineSpace
      )
      if (
        !Number.isFinite(textWidth) ||
        !Number.isFinite(textHeight) ||
        textHeight <= 0
      ) {
        return
      }
      let left = 0
      let top = 0
      let width = textWidth
      const key = `overflowText${index}`

      if (['left', 'right'].includes(dir)) {
        left = dir === 'left' ? 0 : startX + plankWidth + OVERFLOW_OFFSET
        width = textWidth
        top = startY + item.startY - textHeight - 5
        if (top < 0) top = 0
      } else {
        left = startX + item.startX - textWidth
        if (left < 0) left = 0
        top =
          dir === 'top'
            ? startY - data.bigPlankTextHeight - textHeight - OVERFLOW_OFFSET
            : startY + plankHeight + OVERFLOW_OFFSET
      }

      rectMap[key] = {
        part: item,
        rect: { height: textHeight, key, left, top, width },
        text: partText,
      }
    })

    let rectArr = Object.values(rectMap).map(({ rect }) => rect)
    if (['left', 'right'].includes(dir)) {
      rectArr.sort((a, b) => a.top - b.top)
      const adjusted = adjustRectangles(
        rectArr.map((rect) => [
          rect.left,
          rect.top,
          rect.left + rect.width,
          rect.top + rect.height,
          Number(String(rect.key).replace('overflowText', '')),
        ])
      )
      rectArr = adjusted.map((rect) => ({
        height: rect[3] - rect[1],
        key: `overflowText${rect[4]}`,
        left: rect[0],
        top: rect[1],
        width: rect[2] - rect[0],
      }))
    } else {
      rectArr.sort((a, b) => a.left - b.left)
      rectArr = adjustRectanglesLeft(rectArr, startX + plankWidth, 5, dir)
    }

    rectArr.forEach((rect) => {
      const rectInfo = rectMap[rect.key ?? '']
      if (!rectInfo) return
      const { part, text } = rectInfo
      const textWidth = estimateFabricTextWidth(text, partTextFontSize)
      const textHeight = estimateFabricTextHeight(
        text,
        partTextFontSize,
        lineSpace
      )
      const textBottomY = rect.top + textHeight
      let x2 = 0
      if (['left', 'right'].includes(dir)) {
        x2 = dir === 'left' ? part.partCenterX - 5 : part.partCenterX + 5
      } else {
        x2 = dir === 'top' ? rect.left : rect.left + textWidth
      }
      let x3 = 0
      if (dir === 'right') {
        x3 = rect.left + textWidth
      } else if (['top', 'bottom'].includes(dir)) {
        x3 = dir === 'top' ? rect.left + textWidth : rect.left
      }

      addLine(
        dxf,
        canvasH,
        { x: part.partCenterX, y: part.partCenterY },
        { x: x2, y: textBottomY },
        profile.layerMap[DxfLayerRole.OverflowLine].name
      )
      addLine(
        dxf,
        canvasH,
        { x: x2, y: textBottomY },
        { x: x3, y: textBottomY },
        profile.layerMap[DxfLayerRole.OverflowLine].name
      )
      if (['left', 'right'].includes(dir)) {
        addMTextWithStyle(
          dxf,
          canvasH,
          rect.left,
          rect.top,
          partTextHeight,
          text,
          {
            layerName: profile.layerMap[DxfLayerRole.OverflowText].name,
            width: rect.width,
          }
        )
      } else {
        addTextWithStyle(
          dxf,
          canvasH,
          rect.left,
          rect.top,
          partTextHeight,
          text,
          {
            layerName: profile.layerMap[DxfLayerRole.OverflowText].name,
          }
        )
      }
    })
  })
}

function createPaibanImgDxfWithProfile(
  template: any,
  bigPlank: any[],
  /** Trusted, previously validated profile (NOT a public API boundary). */
  profile: DxfProfile,
  config: DxfExportConfig = {}
): string[] {
  const results: string[] = []
  const NC_SETTING = cloneDeep(store.state.ncSetting)
  const copyBigPlank = cloneDeep(bigPlank)

  const threeD = has3D(profile)
  const circleThick = threeD

  /** Resolve a layer name from the active profile. */
  const L = (role: DxfLayerRole): string => profile.layerMap[role].name

  const dxf = new DxfWriter()
  dxf.setUnits(Units.Millimeters)
  addChineseStyle(dxf)
  addStandardLayers(dxf, profile)

  const templateH = template.height
  const total = config.total ?? copyBigPlank.length
  const onePagePlankCount = copyBigPlank.length
  const partTextFontSize = getSafePartTextFontSize(template.part.fontSize)
  const partTextLineSpace = getPartTextLineSpace(template.part.lineSpace)
  const layouts: PlankLayout[] = []

  for (const [index, sourcePlank] of copyBigPlank.entries()) {
    const plank = cloneDeep(sourcePlank)
    const originPlank = cloneDeep(plank)
    if (isTrueRotate(template.bigPlank.rotate)) {
      rotateBigPlank(
        plank,
        normalizeRotate(template.bigPlank.rotate) as 90 | 180 | 270
      )
    }

    const [rotatedMinX, rotatedMinY] = getPathMinMaxPoint(plank.path)
    let finalPath = cloneDeep(plank.path)
    if (
      plank.surplusInfo?.shape === 'lshape' &&
      NC_SETTING.labelOverturn &&
      NC_SETTING?.emergence_setting_key === 'TURNBLANK' &&
      plank.ableToggle
    ) {
      finalPath = rollLShapePlank(finalPath, NC_SETTING.xyReverse)
    }
    const [finalMinX, finalMinY, finalMaxX, finalMaxY] =
      getPathMinMaxPoint(finalPath)

    const bigPlankText = handleBigPlankText(
      originPlank,
      template.bigPlank.code,
      total
    )
    const bigPlankTextHeight = estimateFabricTextHeight(
      bigPlankText,
      template.bigPlank.fontSize,
      template.bigPlank.lineSpace || 1
    )
    layouts[index] = {
      plank,
      originPlank,
      finalMinX,
      finalMinY,
      finalPath,
      finalPlankHeight: finalMaxY - finalMinY,
      finalPlankWidth: finalMaxX - finalMinX,
      rotatedMinX,
      rotatedMinY,
      startX: 0,
      startY: bigPlankTextHeight,
      bigPlankTextHeight,
    }
  }

  if (onePagePlankCount > 1) {
    let currentX = MULTI_PAGE_START_X
    for (const layout of layouts) {
      layout.startX = currentX
      currentX += layout.finalPlankWidth + MULTI_PLANK_GAP
    }
  }

  let maxYExtent = templateH
  for (const layout of layouts) {
    maxYExtent = Math.max(maxYExtent, layout.startY + layout.finalPlankHeight)
  }
  const canvasH = maxYExtent + CANVAS_PADDING

  for (const layout of layouts) {
    const {
      plank,
      originPlank,
      finalMinX,
      finalMinY,
      finalPath,
      finalPlankHeight,
      finalPlankWidth,
      rotatedMinX,
      rotatedMinY,
      startX,
      startY,
      bigPlankTextHeight,
    } = layout
    const bigPlankText = handleBigPlankText(
      originPlank,
      template.bigPlank.code,
      total
    )
    const bigPlankTextCenterX = startX + finalPlankWidth / 2
    addMultilineTextWithStyle(
      dxf,
      canvasH,
      bigPlankTextCenterX,
      0,
      getDxfTextHeight(template.bigPlank.fontSize),
      bigPlankText,
      {
        horizontalAlignment: TextHorizontalAlignment.Center,
        layerName: L(DxfLayerRole.BigPlankText),
        lineSpace: template.bigPlank.lineSpace || 1,
        secondAlignmentPoint: point3d(bigPlankTextCenterX, canvasH),
      }
    )

    addClosedPath(
      dxf,
      canvasH,
      finalPath.map((point) => ({
        x: startX + point.x - finalMinX,
        y: startY + point.y - finalMinY,
      })),
      L(DxfLayerRole.BigPlank),
      threeD ? outlineSpec(Number(plank.thick)) : undefined
    )

    const bigPlankCenterX = startX + finalPlankWidth / 2
    const bigPlankCenterY = startY + finalPlankHeight / 2
    const overflowPart: Record<string, any[]> = {
      top: [],
      bottom: [],
      left: [],
      right: [],
    }
    const partBaseX = startX - rotatedMinX
    const partBaseY = startY - rotatedMinY

    plank.parts.forEach((rawItem: any) => {
      const item = transformData(rawItem, store) as unknown as DxfPart
      const px = item.startX + partBaseX
      const py = item.startY + partBaseY

      // 已验证的板厚，作为所有 3D 深度/高度的单一数据源
      const panelThick =
        typeof item.thick === 'number' &&
        Number.isFinite(item.thick) &&
        item.thick > 0
          ? item.thick
          : undefined
      const outlineSpec3D = threeD ? outlineSpec(panelThick) : undefined

      if (item.path) {
        drawPathList(
          dxf,
          canvasH,
          item.path,
          item,
          partBaseX,
          partBaseY,
          L(DxfLayerRole.Parts),
          outlineSpec3D
        )
      } else {
        addRect(
          dxf,
          canvasH,
          px,
          py,
          item.rect.width,
          item.rect.height,
          L(DxfLayerRole.Parts),
          outlineSpec3D
        )
      }

      const pw = Number(item.rect?.width)
      const ph = Number(item.rect?.height)
      if (
        partTextFontSize != null &&
        Number.isFinite(pw) &&
        pw > 0 &&
        Number.isFinite(ph) &&
        ph > 0
      ) {
        const partText = handlePartText(item, template.part.code)
        const partDxfTextHeight = getDxfTextHeight(
          partTextFontSize * DXF_PART_TEXT_SCALE
        )
        const partTextPadding = getPartTextPadding(pw, ph, partDxfTextHeight)
        const partTextAvailableWidth = pw - partTextPadding * 2
        const partTextAvailableHeight = ph - partTextPadding * 2

        if (partTextAvailableWidth > 0 && partTextAvailableHeight > 0) {
          const wrappedPartText = wrapTextByFabric(
            partText,
            partTextAvailableWidth,
            template.part,
            partTextFontSize,
            partTextLineSpace
          )
          const partTextHeight = estimateFabricTextHeight(
            wrappedPartText,
            partTextFontSize,
            partTextLineSpace
          )
          const partTextDxfHeight = estimateFabricTextHeight(
            wrappedPartText,
            partDxfTextHeight,
            partTextLineSpace
          )
          const partTextWidth = estimateFabricTextWidth(
            wrappedPartText,
            partDxfTextHeight
          )

          if (
            Number.isFinite(partTextHeight) &&
            partTextHeight - 1 > ph &&
            template.part.showOverflow &&
            onePagePlankCount <= 1
          ) {
            const overflowItem = {
              ...item,
              startX: item.startX - rotatedMinX,
              startY: item.startY - rotatedMinY,
              partCenterX: px + item.rect.width / 2,
              partCenterY: py + item.rect.height / 2,
            }
            if (finalPlankWidth >= finalPlankHeight) {
              const isTop = overflowItem.partCenterY <= bigPlankCenterY
              const dir = isTop ? 'top' : 'bottom'
              overflowPart[dir].push(overflowItem)
            } else {
              const isLeft = overflowItem.partCenterX <= bigPlankCenterX
              const dir = isLeft ? 'left' : 'right'
              overflowPart[dir].push(overflowItem)
            }
          } else {
            const widthScale =
              partTextWidth > 0 ? partTextAvailableWidth / partTextWidth : 1
            const heightScale =
              partTextDxfHeight > 0
                ? partTextAvailableHeight / partTextDxfHeight
                : 1
            const fittedPartDxfTextHeight =
              partDxfTextHeight * Math.min(1, widthScale, heightScale)

            if (
              Number.isFinite(partTextWidth) &&
              Number.isFinite(partTextDxfHeight) &&
              partTextDxfHeight > 0 &&
              Number.isFinite(fittedPartDxfTextHeight) &&
              fittedPartDxfTextHeight > 0
            ) {
              addMultilineTextWithStyle(
                dxf,
                canvasH,
                px + partTextPadding,
                py + partTextPadding,
                fittedPartDxfTextHeight,
                wrappedPartText,
                {
                  layerName: L(DxfLayerRole.PartText),
                  lineSpace: partTextLineSpace,
                }
              )
            }
          }
        }
      }

      if (template.part.showHoleSlot) {
        item.holes
          .filter((it) => it.side === 1)
          .forEach((it) => {
            const { diameter, center } = it
            const radius = diameter / 2
            const cx = item.startX + center.x + partBaseX
            const cy = item.startY + center.y + partBaseY
            const spec = threeD ? frontFeatureSpec(it.deep) : undefined
            const circle = dxf.modelSpace.addCircle(
              point3d(cx, canvasH - cy, spec?.elevation ?? 0),
              radius,
              { layerName: L(DxfLayerRole.HolesFront) }
            )
            if (spec) {
              if (circleThick) {
                applyCircleThickness(circle, spec.thickness)
              }
            }
          })
        item.holes
          .filter((it) => it.side === -1)
          .forEach((it) => {
            const { diameter, center } = it
            const radius = diameter / 2
            const cx = item.startX + center.x + partBaseX
            const cy = item.startY + center.y + partBaseY
            const spec = threeD
              ? backFeatureSpec(it.deep, panelThick)
              : undefined
            const circle = dxf.modelSpace.addCircle(
              point3d(cx, canvasH - cy, spec?.elevation ?? 0),
              radius,
              { layerName: L(DxfLayerRole.HolesBack) }
            )
            if (spec) {
              if (circleThick) {
                applyCircleThickness(circle, spec.thickness)
              }
            }
          })
        item.slots
          .filter((it) => [1, -1].includes(it.side))
          .forEach((it) => {
            drawSlot(
              dxf,
              canvasH,
              item,
              it,
              partBaseX,
              partBaseY,
              getSlotLayer(
                it,
                L(DxfLayerRole.SlotsFront),
                L(DxfLayerRole.SlotsBack)
              ),
              threeD
            )
          })
        item.curveHoles
          ?.filter((it) => it.side === 1)
          .forEach((it) => {
            drawPathList(
              dxf,
              canvasH,
              [it.path],
              item,
              partBaseX,
              partBaseY,
              L(DxfLayerRole.CurveHolesFront),
              threeD ? frontFeatureSpec(it.deep) : undefined
            )
          })
        item.curveHoles
          ?.filter((it) => it.side === -1)
          .forEach((it) => {
            drawPathList(
              dxf,
              canvasH,
              [it.path],
              item,
              partBaseX,
              partBaseY,
              L(DxfLayerRole.CurveHolesBack),
              threeD ? backFeatureSpec(it.deep, panelThick) : undefined
            )
          })
        item.millInfo
          ?.filter((it) => it.side === 1)
          .forEach((it) => {
            drawPathList(
              dxf,
              canvasH,
              [it.shape],
              item,
              partBaseX,
              partBaseY,
              L(DxfLayerRole.MillInfoFront),
              threeD ? frontFeatureSpec(it.depth) : undefined
            )
          })
        item.millInfo
          ?.filter((it) => it.side === -1)
          .forEach((it) => {
            drawPathList(
              dxf,
              canvasH,
              [it.shape],
              item,
              partBaseX,
              partBaseY,
              L(DxfLayerRole.MillInfoBack),
              threeD ? backFeatureSpec(it.depth, panelThick) : undefined
            )
          })
      }
    })

    // ---- 侧孔（仅 profile.sideDrill，且受 showHoleSlot 控制）----
    if (profile.sideDrill && template.part.showHoleSlot) {
      const EPS = 0.01
      let viewSideHoleLayerAdded = false

      plank.parts.forEach((rawItem: any) => {
        const item = transformData(rawItem, store) as unknown as DxfPart
        if (!Array.isArray(item.sholes) || item.sholes.length === 0) return

        const oPathRaw = item.oPath as PointType[][] | null | undefined
        const hasOPath = oPathRaw != null

        // oRect 基础校验
        const oRectRaw = (item as any).oRect
        if (!hasOPath) {
          if (!oRectRaw || typeof oRectRaw !== 'object')
            throw new Error(`[dxf-export] oRect 缺失`)
          const rx = oRectRaw.x,
            ry = oRectRaw.y,
            rw = oRectRaw.width,
            rh = oRectRaw.height
          if (typeof rx !== 'number' || !Number.isFinite(rx))
            throw new Error(`[dxf-export] oRect.x 非法`)
          if (typeof ry !== 'number' || !Number.isFinite(ry))
            throw new Error(`[dxf-export] oRect.y 非法`)
          if (typeof rw !== 'number' || !Number.isFinite(rw) || rw <= 0)
            throw new Error(`[dxf-export] oRect.w 非法`)
          if (typeof rh !== 'number' || !Number.isFinite(rh) || rh <= 0)
            throw new Error(`[dxf-export] oRect.h 非法`)
        }

        // oPath: 安全验证后求 bounds
        let oRect: {
          L: number
          T: number
          R: number
          B: number
          W: number
          H: number
        }
        if (hasOPath) {
          if (
            !Array.isArray(oPathRaw) ||
            oPathRaw.length !== 1 ||
            !Array.isArray(oPathRaw[0]) ||
            oPathRaw[0].length < 3
          )
            throw new Error(`[dxf-export] oPath 须为单一非空环`)
          const ring = oPathRaw[0]
          for (let i = 0; i < ring.length; i++) {
            const p = ring[i]
            if (
              !p ||
              typeof p !== 'object' ||
              typeof p.x !== 'number' ||
              typeof p.y !== 'number' ||
              !Number.isFinite(p.x) ||
              !Number.isFinite(p.y)
            )
              throw new Error(`[dxf-export] oPath 含非法点`)
          }
          let mnX = Infinity,
            mxX = -Infinity,
            mnY = Infinity,
            mxY = -Infinity
          for (const p of ring) {
            if (p.x < mnX) mnX = p.x
            if (p.x > mxX) mxX = p.x
            if (p.y < mnY) mnY = p.y
            if (p.y > mxY) mxY = p.y
          }
          oRect = { L: mnX, T: mnY, R: mxX, B: mxY, W: mxX - mnX, H: mxY - mnY }
        } else {
          oRect = {
            L: oRectRaw.x,
            T: oRectRaw.y,
            R: oRectRaw.x + oRectRaw.width,
            B: oRectRaw.y + oRectRaw.height,
            W: oRectRaw.width,
            H: oRectRaw.height,
          }
        }

        const ox = item.startX + partBaseX
        const oy = item.startY + partBaseY

        for (const shole of item.sholes) {
          const side = Number(shole.side) as 1 | 2 | 3 | 4
          const { ocenter, center, deep, diameter } = shole
          if (side < 1 || side > 4 || !Number.isInteger(side))
            throw new Error(`[dxf-export] 侧孔 side 非法`)
          if (
            typeof item.thick !== 'number' ||
            !Number.isFinite(item.thick) ||
            item.thick <= 0
          )
            throw new Error(`[dxf-export] 侧孔 thick 无效`)
          const cx = (center as any)?.x,
            cy = (center as any)?.y,
            cz = (center as any)?.z
          if (
            typeof cx !== 'number' ||
            typeof cy !== 'number' ||
            typeof cz !== 'number' ||
            !Number.isFinite(cx) ||
            !Number.isFinite(cy) ||
            !Number.isFinite(cz)
          )
            throw new Error(`[dxf-export] 侧孔 center 非法`)
          if (cz <= 0) throw new Error(`[dxf-export] 侧孔 center.z 须>0`)
          if (cy < -EPS || cy > item.thick + EPS)
            throw new Error(`[dxf-export] 侧孔 center.y 超出厚`)
          if (cx < -EPS || cx > cz + EPS)
            throw new Error(`[dxf-export] 侧孔 center.x 超出 [0,cz]`)
          if (typeof deep !== 'number' || !Number.isFinite(deep) || deep <= 0)
            throw new Error(`[dxf-export] 侧孔 deep 无效`)
          const diam =
            typeof diameter === 'number' &&
            Number.isFinite(diameter) &&
            diameter > 0
              ? diameter
              : 0
          if (diam <= 0) throw new Error(`[dxf-export] 侧孔 diameter 无效`)
          if (
            typeof ocenter?.x !== 'number' ||
            typeof ocenter?.y !== 'number' ||
            !Number.isFinite(ocenter.x) ||
            !Number.isFinite(ocenter.y)
          )
            throw new Error(`[dxf-export] 侧孔 ocenter 非法`)

          // span 校验
          const span = side === 1 || side === 3 ? oRect.H : oRect.W
          if (Math.abs(cz - span) > EPS)
            throw new Error(`[dxf-export] 侧孔 cz≠span`)

          // 规范锚点（原始坐标）
          let anchorX: number, anchorY: number
          if (side === 1) {
            anchorX = oRect.L
            anchorY = oRect.B - cx
          } else if (side === 2) {
            anchorX = oRect.R - cx
            anchorY = oRect.B
          } else if (side === 3) {
            anchorX = oRect.R
            anchorY = oRect.T + cx
          } else {
            anchorX = oRect.L + cx
            anchorY = oRect.T
          }

          if (hasOPath) {
            // oPath：cardinal 投影（使用 bounds 极端坐标推导切线）
            const ring = oPathRaw![0]
            const poly = normalizePolygon(
              ring.map((p) => ({ x: p.x, y: p.y })),
              EPS
            )
            // 切线：s1=maxY-cx, s2=maxX-cx, s3=minY+cx, s4=minX+cx
            const isHoriz = side === 1 || side === 3
            let tanCoord: number
            if (side === 1) tanCoord = oRect.B - cx
            else if (side === 2) tanCoord = oRect.R - cx
            else if (side === 3) tanCoord = oRect.T + cx
            else tanCoord = oRect.L + cx
            const hits = intersectCardinal(poly, tanCoord, isHoriz, EPS)
            if (hits.length === 0)
              throw new Error(`[dxf-export] 侧孔投影无交点`)

            let proj: { x: number; y: number } | null = null
            if (side === 1) {
              let best = Infinity
              for (const h of hits) {
                if (h.x < best - EPS) {
                  best = h.x
                  proj = h
                }
              }
            } else if (side === 2) {
              let best = -Infinity
              for (const h of hits) {
                if (h.y > best + EPS) {
                  best = h.y
                  proj = h
                }
              }
            } else if (side === 3) {
              let best = -Infinity
              for (const h of hits) {
                if (h.x > best + EPS) {
                  best = h.x
                  proj = h
                }
              }
            } else {
              let best = Infinity
              for (const h of hits) {
                if (h.y < best - EPS) {
                  best = h.y
                  proj = h
                }
              }
            }
            if (!proj) throw new Error(`[dxf-export] 侧孔极值不唯一`)

            const deduped = dedupePoints(hits, EPS)
            const tieCheck =
              side === 1 || side === 3
                ? deduped.filter((p) => Math.abs(p.x - proj!.x) <= EPS).length
                : deduped.filter((p) => Math.abs(p.y - proj!.y) <= EPS).length
            if (tieCheck > 1) throw new Error(`[dxf-export] 侧孔极值平局`)

            // 投影即为规范锚点；仅比较 ocenter
            anchorX = proj.x
            anchorY = proj.y
            if (
              Math.abs(ocenter.x - anchorX) > EPS ||
              Math.abs(ocenter.y - anchorY) > EPS
            )
              throw new Error(`[dxf-export] 侧孔 ocenter≠投影`)
          } else {
            // oRect：比较 ocenter 与 bbox 锚点
            if (
              Math.abs(ocenter.x - anchorX) > EPS ||
              Math.abs(ocenter.y - anchorY) > EPS
            )
              throw new Error(`[dxf-export] 侧孔 ocenter≠锚点`)
          }

          // 局部矩形
          let rL: number, rT: number, rW: number, rH: number
          if (side === 1) {
            rL = anchorX
            rT = anchorY - diam / 2
            rW = deep
            rH = diam
          } else if (side === 2) {
            rL = anchorX - diam / 2
            rT = anchorY - deep
            rW = diam
            rH = deep
          } else if (side === 3) {
            rL = anchorX - deep
            rT = anchorY - diam / 2
            rW = deep
            rH = diam
          } else {
            rL = anchorX - diam / 2
            rT = anchorY
            rW = diam
            rH = deep
          }

          if (hasOPath) {
            const poly = normalizePolygon(
              oPathRaw![0].map((p) => ({ x: p.x, y: p.y })),
              EPS
            )
            const rc = { x: rL + rW / 2, y: rT + rH / 2 }
            const corners = [
              { x: rL, y: rT },
              { x: rL + rW, y: rT },
              { x: rL, y: rT + rH },
              { x: rL + rW, y: rT + rH },
            ]
            for (const c of corners) {
              if (!pointInPolygon(c, poly))
                throw new Error(`[dxf-export] 侧孔角点在路径外`)
            }
            if (!pointInPolygon(rc, poly))
              throw new Error(`[dxf-export] 侧孔中心在路径外`)
            for (let i = 0; i < poly.length - 1; i++) {
              if (
                liangBarskyClip(
                  poly[i].x,
                  poly[i].y,
                  poly[i + 1].x,
                  poly[i + 1].y,
                  rL,
                  rT,
                  rL + rW,
                  rT + rH
                )
              ) {
                throw new Error(`[dxf-export] 侧孔被路径凹口侵入`)
              }
            }
          } else {
            if (
              rL < oRect.L - EPS ||
              rL + rW > oRect.R + EPS ||
              rT < oRect.T - EPS ||
              rT + rH > oRect.B + EPS
            )
              throw new Error(`[dxf-export] 侧孔超出 oRect`)
          }

          const prL = ox + rL,
            prT = oy + rT
          const prW = rW,
            prH = rH
          const layerDistance = Number(cy.toFixed(3)).toString()
          const layerName = `Drill_Side_${layerDistance}`
          dxf.addLayer(layerName, 4, 'Continuous')
          dxf.modelSpace.addRectangle(
            point2d(prL, canvasH - prT),
            point2d(prL + prW, canvasH - (prT + prH)),
            { layerName }
          )

          // ---- 侧孔视图圆（WCS LINE 近似，仅供展示，不可与加工矩形混淆）----
          if (profile.sideDrillView) {
            if (!viewSideHoleLayerAdded) {
              dxf.addLayer('VIEW_SIDE_HOLE', 4, 'Continuous')
              viewSideHoleLayerAdded = true
            }
            const SEGS = 24
            const radius = diam / 2
            const VIEW_SIDE_HOLE = 'VIEW_SIDE_HOLE'
            const wcsCenterX = ox + anchorX
            const wcsCenterY = canvasH - (oy + anchorY)
            const wcsCenterZ = -cy
            const isYZ = side === 1 || side === 3

            for (let seg = 0; seg < SEGS; seg++) {
              const a0 = (2 * Math.PI * seg) / SEGS
              const a1 = (2 * Math.PI * (seg + 1)) / SEGS
              const cos0 = radius * Math.cos(a0)
              const sin0 = radius * Math.sin(a0)
              const cos1 = radius * Math.cos(a1)
              const sin1 = radius * Math.sin(a1)

              let p0: any
              let p1: any
              if (isYZ) {
                // side1 / side3: fixed X, circle spans YZ
                p0 = point3d(wcsCenterX, wcsCenterY + cos0, wcsCenterZ + sin0)
                p1 = point3d(wcsCenterX, wcsCenterY + cos1, wcsCenterZ + sin1)
              } else {
                // side2 / side4: fixed Y, circle spans XZ
                p0 = point3d(wcsCenterX + cos0, wcsCenterY, wcsCenterZ + sin0)
                p1 = point3d(wcsCenterX + cos1, wcsCenterY, wcsCenterZ + sin1)
              }
              dxf.modelSpace.addLine(p0, p1, { layerName: VIEW_SIDE_HOLE })
            }
          }
        }
      })
    }

    // ---- 侧槽（Groove side slots，仅 profile.sideDrill + showHoleSlot，3D face）----
    if (profile.sideDrill && template.part.showHoleSlot) {
      const GROOVE_EPS = 0.01

      plank.parts.forEach((rawItem: any) => {
        const item = transformData(rawItem, store) as unknown as DxfPart
        if (!Array.isArray(item.sslots) || item.sslots.length === 0) return

        const ox = item.startX + partBaseX
        const oy = item.startY + partBaseY
        const rw = item.oRect.width
        const rh = item.oRect.height
        if (
          typeof rw !== 'number' ||
          !Number.isFinite(rw) ||
          rw <= 0 ||
          typeof rh !== 'number' ||
          !Number.isFinite(rh) ||
          rh <= 0
        )
          throw new Error('[dxf-export] 侧槽依赖有效 rect 尺寸')

        for (const sslot of item.sslots) {
          const side = Number(sslot.side) as 1 | 2 | 3 | 4
          if (side < 1 || side > 4 || !Number.isInteger(side))
            throw new Error('[dxf-export] 侧槽 side 须为 1..4')

          const { pt1, pt2, width, deep } = sslot

          // 校验 pt：须含有限 x/y/z
          const ensurePt = (p: any, label: string) => {
            if (
              !p ||
              typeof p !== 'object' ||
              typeof p.x !== 'number' ||
              !Number.isFinite(p.x) ||
              typeof p.y !== 'number' ||
              !Number.isFinite(p.y) ||
              typeof p.z !== 'number' ||
              !Number.isFinite(p.z)
            )
              throw new Error(`[dxf-export] 侧槽 ${label} 非法`)
          }
          ensurePt(pt1, 'pt1')
          ensurePt(pt2, 'pt2')

          const p1 = pt1 as any
          const p2 = pt2 as any

          // pt1.z 与 pt2.z 须为相同有限正值
          if (p1.z !== p2.z) throw new Error('[dxf-export] 侧槽 pt1.z ≠ pt2.z')
          if (p1.z <= 0) throw new Error('[dxf-export] 侧槽 pt.z 须 > 0')

          // pt.x 范围：[0, pt.z]（EPS 容差）
          if (p1.x < -GROOVE_EPS || p1.x > p1.z + GROOVE_EPS)
            throw new Error(`[dxf-export] 侧槽 pt1.x=${p1.x} 超出 [0,${p1.z}]`)
          if (p2.x < -GROOVE_EPS || p2.x > p1.z + GROOVE_EPS)
            throw new Error(`[dxf-export] 侧槽 pt2.x=${p2.x} 超出 [0,${p1.z}]`)

          // 边长一致性：pt.z 须与对应边界边长一致
          const edgeLen = side === 1 || side === 3 ? rh : rw
          if (Math.abs(p1.z - edgeLen) > GROOVE_EPS)
            throw new Error(`[dxf-export] 侧槽 pt.z=${p1.z} ≠ 边长=${edgeLen}`)

          if (
            typeof width !== 'number' ||
            !Number.isFinite(width) ||
            width <= 0
          )
            throw new Error('[dxf-export] 侧槽 width 无效')
          if (typeof deep !== 'number' || !Number.isFinite(deep) || deep <= 0)
            throw new Error('[dxf-export] 侧槽 deep 无效')

          // Z 坐标：板厚方向中点 ± width/2，板顶 z=0 板厚向 -Z
          const centerY = (p1.y + p2.y) / 2
          const zTop = -(centerY - width / 2)
          const zBottom = -(centerY + width / 2)

          // 从 pt 推导槽底在 DXF 全局坐标中的位置
          const groovePt = (pt: { x: number }) => {
            let dxfX: number
            let dxfY: number
            if (side === 1) {
              dxfX = ox + deep
              dxfY = canvasH - oy - rh + pt.x
            } else if (side === 2) {
              dxfX = ox + rw - pt.x
              dxfY = canvasH - oy - rh + deep
            } else if (side === 3) {
              dxfX = ox + rw - deep
              dxfY = canvasH - oy - pt.x
            } else {
              // side 4
              dxfX = ox + pt.x
              dxfY = canvasH - oy - deep
            }
            return { x: dxfX, y: dxfY }
          }

          const gp1 = groovePt(p1)
          const gp2 = groovePt(p2)

          // 构建槽底矩形面（垂直于板面），外向绕序
          const v1 = point3d(gp1.x, gp1.y, zTop)
          const v2 = point3d(gp2.x, gp2.y, zTop)
          const v3 = point3d(gp2.x, gp2.y, zBottom)
          const v4 = point3d(gp1.x, gp1.y, zBottom)

          const layerName = `Groove_Side_${side}`
          dxf.addLayer(layerName, 4, 'Continuous')
          dxf.modelSpace.add3dFace(v4, v3, v2, v1, {
            layerName,
          })
        }
      })
    }

    drawOverflowTextAndLine(
      dxf,
      canvasH,
      overflowPart,
      template,
      {
        bigPlankTextHeight,
        plankHeight: finalPlankHeight,
        plankWidth: finalPlankWidth,
        startX,
        startY,
      },
      profile,
      partTextFontSize,
      partTextLineSpace
    )
  }

  results.push(dxf.stringify())
  return results
}

/** 公共接口：与 exportPaibanImgDxf 使用相同的 profileId 解析逻辑。 */
function createPaibanImgDxf(
  template: any,
  bigPlank: any[],
  config: DxfExportConfig = {}
): string[] {
  const resolution = resolveProfile(config.profileId)
  if (!resolution.ok) {
    throw new Error(`[dxf-export] Invalid profile id "${resolution.id}"`)
  }
  return createPaibanImgDxfWithProfile(
    template,
    bigPlank,
    resolution.profile,
    config
  )
}

export async function exportPaibanImgDxf(
  template: any,
  bigPlankList: any[],
  config: DxfExportConfig = { total: 1 }
): Promise<JSZip> {
  // 在分页之前解析配置（含空列表场景）
  const resolution = resolveProfile(config.profileId)
  if (!resolution.ok) {
    throw new Error(`[dxf-export] Invalid profile id "${resolution.id}"`)
  }
  const profile = resolution.profile

  const onePageNum = config.onePageNum ?? (bigPlankList.length || 1)
  const chunkPaibanData = chunkArray(bigPlankList, onePageNum)
  const zip = config.zip ?? new JSZip()
  const folderName = config.folderName ? `${config.folderName}/` : ''

  chunkPaibanData.forEach((chunk, pageIndex) => {
    const dxfStrings = createPaibanImgDxfWithProfile(
      template,
      chunk,
      profile,
      config
    )
    dxfStrings.forEach((dxfStr, idx) => {
      zip.file(`${folderName}排版图${pageIndex + idx + 1}.dxf`, dxfStr)
    })
  })

  if (config.save !== false) {
    const blob = await zip.generateAsync({ type: 'blob' })
    FileSaver.saveAs(blob, `${config.fileName ?? '排版图DXF'}.zip`)
  }
  return zip
}

export { createPaibanImgDxf }
